import React, { useState, useEffect } from 'react';

interface BackgroundCheckStepProps {
  data?: any;
  profileData?: any; // Profile data to get candidate info
  onComplete: (data: any) => void;
  onNext: () => void;
  onPrevious: () => void;
}

interface BackgroundCheckData {
  permissionGranted: boolean;
  fullName: string;
  dateOfBirth: string;
  ssn: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  hasPreviousAddress: boolean;
  previousAddress?: string;
  previousCity?: string;
  previousState?: string;
  previousZipCode?: string;
  submitted: boolean;
  submittedAt?: string;
}

const BackgroundCheckStep: React.FC<BackgroundCheckStepProps> = ({ 
  data, 
  profileData,
  onComplete, 
  onNext, 
  onPrevious 
}) => {
  const [formData, setFormData] = useState<BackgroundCheckData>({
    permissionGranted: false,
    fullName: '',
    dateOfBirth: '',
    ssn: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    hasPreviousAddress: false,
    previousAddress: '',
    previousCity: '',
    previousState: '',
    previousZipCode: '',
    submitted: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Pre-fill with existing data if available
    if (data) {
      setFormData(prev => ({
        ...prev,
        ...data,
      }));
    } else if (profileData) {
      // Pre-fill with profile data
      setFormData(prev => ({
        ...prev,
        fullName: `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim(),
        dateOfBirth: profileData.dateOfBirth || '',
      }));
    }
  }, [data, profileData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.permissionGranted) {
      newErrors.permissionGranted = 'You must grant permission to proceed with the background check';
    }

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    }

    if (!formData.ssn.trim()) {
      newErrors.ssn = 'Social Security Number is required';
    } else if (!/^\d{3}-?\d{2}-?\d{4}$/.test(formData.ssn)) {
      newErrors.ssn = 'Please enter a valid SSN (format: XXX-XX-XXXX)';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Street address is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode)) {
      newErrors.zipCode = 'Please enter a valid ZIP code';
    }

    // Validate previous address if checkbox is checked
    if (formData.hasPreviousAddress) {
      if (!formData.previousAddress?.trim()) {
        newErrors.previousAddress = 'Previous street address is required';
      }
      if (!formData.previousCity?.trim()) {
        newErrors.previousCity = 'Previous city is required';
      }
      if (!formData.previousState?.trim()) {
        newErrors.previousState = 'Previous state is required';
      }
      if (!formData.previousZipCode?.trim()) {
        newErrors.previousZipCode = 'Previous ZIP code is required';
      } else if (!/^\d{5}(-\d{4})?$/.test(formData.previousZipCode)) {
        newErrors.previousZipCode = 'Please enter a valid ZIP code';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const submitData = {
        ...formData,
        submitted: true,
        submittedAt: new Date().toISOString(),
      };

      await onComplete(submitData);
    } catch (err: any) {
      setErrors({ submit: err.message || 'Failed to submit. Please try again.' });
      console.error('Error submitting background check:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatSSN = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 9)}`;
  };

  const handleSSNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatSSN(e.target.value);
    setFormData(prev => ({ ...prev, ssn: formatted }));
    if (errors.ssn) {
      setErrors(prev => ({ ...prev, ssn: '' }));
    }
  };

  if (formData.submitted) {
    return (
      <div className="p-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-green-800">Background Check Information Submitted</h3>
              <p className="mt-2 text-sm text-green-700">
                Your background check information has been submitted successfully. Our background check 
                coordinator will process your information and contact you if any additional information is needed.
              </p>
              {formData.submittedAt && (
                <p className="mt-2 text-xs text-green-600">
                  Submitted on: {new Date(formData.submittedAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Admin Status */}
        {profileData?.backgroundCheckStatus && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Current Status</h3>
            <div className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Background Check Status</dt>
                <dd className="mt-1">
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    profileData.backgroundCheckStatus === 'received' ? 'bg-green-100 text-green-800' :
                    profileData.backgroundCheckStatus === 'submitted' ? 'bg-blue-100 text-blue-800' :
                    profileData.backgroundCheckStatus === 'started' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {profileData.backgroundCheckStatus === 'not_started' && 'Not Started'}
                    {profileData.backgroundCheckStatus === 'started' && 'In Progress'}
                    {profileData.backgroundCheckStatus === 'submitted' && 'Submitted for Processing'}
                    {profileData.backgroundCheckStatus === 'received' && 'Received ✓'}
                  </span>
                </dd>
              </div>
              {profileData.backgroundCheckStatusUpdatedAt && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(profileData.backgroundCheckStatusUpdatedAt).toLocaleString()}
                  </dd>
                </div>
              )}
              {profileData.backgroundCheckNotes && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Notes from Coordinator</dt>
                  <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 p-3 rounded">
                    {profileData.backgroundCheckNotes}
                  </dd>
                </div>
              )}
            </div>
            
            {profileData.backgroundCheckStatus !== 'received' && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <svg className="inline h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Your background check is being processed. This typically takes 7-10 business days.
                </p>
              </div>
            )}
            
            {profileData.backgroundCheckStatus === 'received' && (
              <div className="mt-4 p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-800">
                  <svg className="inline h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Your background check has been completed and received!
                </p>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between">
          <button
            type="button"
            onClick={onPrevious}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={onNext}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Background Check Authorization</h2>
        <p className="text-gray-600">
          As part of our screening process, we require all church planting candidates to complete a background check. 
          Please review the information below and provide your authorization.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">What You Need to Know:</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start">
            <svg className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>The background check will include criminal history, sex offender registry, and national watch lists</span>
          </li>
          <li className="flex items-start">
            <svg className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>The background check typically takes 3-7 business days to complete</span>
          </li>
          <li className="flex items-start">
            <svg className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>You will be notified once the background check is complete</span>
          </li>
        </ul>
      </div>

      {/* Security Information */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-semibold text-green-900 mb-2">🔒 Your Privacy & Security</h3>
            <p className="text-sm text-green-800 mb-2">
              We take the security of your personal information very seriously:
            </p>
            <ul className="space-y-1 text-xs text-green-800 ml-4">
              <li className="flex items-start">
                <span className="mr-1.5">•</span>
                <span><strong>Encrypted Storage:</strong> Your Social Security Number is encrypted using military-grade AES-256 encryption before being stored in our database</span>
              </li>
              <li className="flex items-start">
                <span className="mr-1.5">•</span>
                <span><strong>Limited Access:</strong> Only authorized administrators can view your SSN, and only when necessary for processing your background check</span>
              </li>
              <li className="flex items-start">
                <span className="mr-1.5">•</span>
                <span><strong>Audit Trail:</strong> Every time your SSN is accessed, the action is logged for security and compliance purposes</span>
              </li>
              <li className="flex items-start">
                <span className="mr-1.5">•</span>
                <span><strong>Secure Transmission:</strong> All data is transmitted over encrypted HTTPS connections</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Permission Checkbox */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start">
            <input
              type="checkbox"
              id="permissionGranted"
              name="permissionGranted"
              checked={formData.permissionGranted}
              onChange={handleInputChange}
              className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
            />
            <label htmlFor="permissionGranted" className="ml-3 text-sm">
              <span className="font-medium text-gray-900">I authorize 242Network to conduct a background check</span>
              <p className="text-gray-700 mt-1">
                I hereby authorize 242Network and its designated agents to conduct a comprehensive background check. 
                I understand that this background check may include criminal history, sex offender registry checks, 
                and other relevant public records. I certify that all information provided is true and accurate to 
                the best of my knowledge.
              </p>
            </label>
          </div>
          {errors.permissionGranted && (
            <p className="mt-2 ml-8 text-sm text-red-600">{errors.permissionGranted}</p>
          )}
        </div>

        {/* Personal Information */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
              Full Legal Name *
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
            {errors.fullName && <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>}
          </div>

          <div>
            <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">
              Date of Birth *
            </label>
            <input
              type="date"
              id="dateOfBirth"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
            {errors.dateOfBirth && <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>}
          </div>

          <div>
            <label htmlFor="ssn" className="block text-sm font-medium text-gray-700">
              Social Security Number *
            </label>
            <input
              type="text"
              id="ssn"
              name="ssn"
              value={formData.ssn}
              onChange={handleSSNChange}
              placeholder="XXX-XX-XXXX"
              maxLength={11}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
            {errors.ssn && <p className="mt-1 text-sm text-red-600">{errors.ssn}</p>}
            <p className="mt-1 text-xs text-green-600 flex items-center">
              <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Encrypted and securely stored
            </p>
          </div>
        </div>

        {/* Current Address Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Current Address</h3>
          
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700">
              Street Address *
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
            {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address}</p>}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                City *
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
              {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
            </div>

            <div>
              <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                State *
              </label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                placeholder="MS"
                maxLength={2}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
              {errors.state && <p className="mt-1 text-sm text-red-600">{errors.state}</p>}
            </div>

            <div>
              <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">
                ZIP Code *
              </label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleInputChange}
                placeholder="12345"
                maxLength={10}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
              {errors.zipCode && <p className="mt-1 text-sm text-red-600">{errors.zipCode}</p>}
            </div>
          </div>
        </div>

        {/* Previous Address Section */}
        <div className="space-y-4 border-t border-gray-200 pt-6">
          <div>
            <div className="flex items-start">
              <input
                type="checkbox"
                id="hasPreviousAddress"
                name="hasPreviousAddress"
                checked={formData.hasPreviousAddress}
                onChange={handleInputChange}
                className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
              />
              <label htmlFor="hasPreviousAddress" className="ml-3">
                <span className="text-sm font-medium text-gray-900">I have lived at a different address within the last 7 years</span>
                <p className="text-xs text-gray-600 mt-1">
                  Many background checks require address history for the past 7 years. Check this box if you have moved within that timeframe.
                </p>
              </label>
            </div>
          </div>

          {formData.hasPreviousAddress && (
            <div className="ml-8 space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="text-base font-medium text-gray-900">Previous Address</h3>
              
              <div>
                <label htmlFor="previousAddress" className="block text-sm font-medium text-gray-700">
                  Street Address *
                </label>
                <input
                  type="text"
                  id="previousAddress"
                  name="previousAddress"
                  value={formData.previousAddress || ''}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                {errors.previousAddress && <p className="mt-1 text-sm text-red-600">{errors.previousAddress}</p>}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label htmlFor="previousCity" className="block text-sm font-medium text-gray-700">
                    City *
                  </label>
                  <input
                    type="text"
                    id="previousCity"
                    name="previousCity"
                    value={formData.previousCity || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  {errors.previousCity && <p className="mt-1 text-sm text-red-600">{errors.previousCity}</p>}
                </div>

                <div>
                  <label htmlFor="previousState" className="block text-sm font-medium text-gray-700">
                    State *
                  </label>
                  <input
                    type="text"
                    id="previousState"
                    name="previousState"
                    value={formData.previousState || ''}
                    onChange={handleInputChange}
                    placeholder="MS"
                    maxLength={2}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  {errors.previousState && <p className="mt-1 text-sm text-red-600">{errors.previousState}</p>}
                </div>

                <div>
                  <label htmlFor="previousZipCode" className="block text-sm font-medium text-gray-700">
                    ZIP Code *
                  </label>
                  <input
                    type="text"
                    id="previousZipCode"
                    name="previousZipCode"
                    value={formData.previousZipCode || ''}
                    onChange={handleInputChange}
                    placeholder="12345"
                    maxLength={10}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  {errors.previousZipCode && <p className="mt-1 text-sm text-red-600">{errors.previousZipCode}</p>}
                </div>
              </div>
            </div>
          )}
        </div>

        {errors.submit && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{errors.submit}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-between">
        <button
          type="button"
          onClick={onPrevious}
          disabled={isLoading}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading || !formData.permissionGranted}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Submitting...
            </>
          ) : (
            'Submit Background Check Authorization'
          )}
        </button>
      </div>
    </div>
  );
};

export default BackgroundCheckStep;



